/**
 * Animated SVG "capability network" — abstract representation of
 * marketing operations connecting platforms. Pure CSS keyframes,
 * no JS animation loop. Respects prefers-reduced-motion via
 * @media in global CSS on .xa-pulse.
 */
const NODES = [
  { id: "core", x: 250, y: 175, r: 22, label: "Marketo", accent: "#6D4DFF" },
  { id: "sfdc", x: 80, y: 90, r: 14, label: "Salesforce" },
  { id: "email", x: 100, y: 260, r: 14, label: "Email" },
  { id: "reports", x: 420, y: 90, r: 14, label: "Reporting" },
  { id: "sync", x: 430, y: 265, r: 14, label: "CRM Sync" },
  { id: "ai", x: 250, y: 40, r: 12, label: "MCP" },
  { id: "ops", x: 250, y: 320, r: 12, label: "Ops" },
];

const LINKS = [
  ["core", "sfdc"],
  ["core", "email"],
  ["core", "reports"],
  ["core", "sync"],
  ["core", "ai"],
  ["core", "ops"],
  ["sfdc", "email"],
  ["reports", "sync"],
];

const byId = (id) => NODES.find((n) => n.id === id);

export default function NetworkGraphic({ className = "" }) {
  return (
    <svg
      viewBox="0 0 500 360"
      className={className}
      role="img"
      aria-label="Marketing operations capability network"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <radialGradient id="ngCoreGrad" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#00D4FF" stopOpacity="0.9" />
          <stop offset="60%" stopColor="#6D4DFF" stopOpacity="0.85" />
          <stop offset="100%" stopColor="#6D4DFF" stopOpacity="0.15" />
        </radialGradient>
        <linearGradient id="ngLinkGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#6D4DFF" stopOpacity="0" />
          <stop offset="45%" stopColor="#00D4FF" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#6D4DFF" stopOpacity="0" />
        </linearGradient>
        <filter id="ngGlow" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* connecting lines */}
      <g stroke="#00D4FF" strokeOpacity="0.28" strokeWidth="1">
        {LINKS.map(([a, b], i) => {
          const A = byId(a);
          const B = byId(b);
          return (
            <line
              key={i}
              x1={A.x}
              y1={A.y}
              x2={B.x}
              y2={B.y}
              strokeDasharray="3 4"
              className="xa-net-line"
              style={{ animationDelay: `${i * 220}ms` }}
            />
          );
        })}
      </g>

      {/* traveling packet dots along a couple of links */}
      {[
        { from: "core", to: "sfdc", dur: 4.2 },
        { from: "core", to: "reports", dur: 5.1 },
        { from: "core", to: "sync", dur: 4.6 },
        { from: "core", to: "ai", dur: 3.8 },
      ].map((p, i) => {
        const A = byId(p.from);
        const B = byId(p.to);
        return (
          <circle key={i} r="2.5" fill="#00D4FF" opacity="0.9">
            <animate
              attributeName="cx"
              values={`${A.x};${B.x};${A.x}`}
              dur={`${p.dur}s`}
              repeatCount="indefinite"
            />
            <animate
              attributeName="cy"
              values={`${A.y};${B.y};${A.y}`}
              dur={`${p.dur}s`}
              repeatCount="indefinite"
            />
            <animate
              attributeName="opacity"
              values="0;0.9;0.9;0"
              dur={`${p.dur}s`}
              repeatCount="indefinite"
            />
          </circle>
        );
      })}

      {/* nodes */}
      {NODES.map((n) => {
        const isCore = n.id === "core";
        return (
          <g key={n.id} filter={isCore ? "url(#ngGlow)" : undefined}>
            {isCore && (
              <>
                <circle cx={n.x} cy={n.y} r={n.r + 22} fill="url(#ngCoreGrad)" opacity="0.35" />
                <circle
                  cx={n.x}
                  cy={n.y}
                  r={n.r + 8}
                  fill="none"
                  stroke="#00D4FF"
                  strokeOpacity="0.7"
                  strokeWidth="1"
                  className="xa-net-pulse"
                />
              </>
            )}
            <circle
              cx={n.x}
              cy={n.y}
              r={n.r}
              fill={isCore ? "url(#ngCoreGrad)" : "#0B1626"}
              stroke={isCore ? "#00D4FF" : "#6D4DFF"}
              strokeOpacity={isCore ? 0.9 : 0.55}
              strokeWidth={isCore ? 1.5 : 1}
            />
            <text
              x={n.x}
              y={n.y + n.r + 16}
              textAnchor="middle"
              fill="#F7F9FC"
              fillOpacity="0.72"
              fontSize={isCore ? 12 : 10}
              fontFamily="IBM Plex Mono, ui-monospace, monospace"
              letterSpacing="0.08em"
            >
              {n.label.toUpperCase()}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
